import { Injectable, inject } from '@angular/core';
import createModule, { QpdfInstance } from '@neslinesli93/qpdf-wasm';
import { assertExists } from 'common';
import { NGXLogger } from 'ngx-logger';
import { Observable, from, map, tap } from 'rxjs';
import { v4 as uuid } from 'uuid';

@Injectable({
  providedIn: 'root',
})
export class QpdfWasmService {
  private instance$?: Observable<QpdfInstance>;

  private logger: NGXLogger = inject(NGXLogger);

  constructor() {}

  /**
   * Initializer for the qpdf library.
   *
   * This way we only load the wasm when it is actually used, rather than
   * upfront at the construction of the service singleton.
   */
  private init() {
    if (typeof this.instance$ === 'undefined') {
      const qpdfInstance$ = from(
        createModule({
          locateFile: () => 'assets/qpdf.wasm',
        })
      ).pipe(
        tap(() => {
          this.logger.debug('qpdf instance initialized.');
        })
      );
      this.instance$ = qpdfInstance$;
    } else {
      this.logger.debug(
        'An initialize of qpdf was requested; however, qpdf-wasm was already initialized.'
      );
    }
  }

  /**
   * Attempts to reprocess a pdf for a repair effect.
   *
   * Just opens the file in qpdf and rewrites it; write action should repair.
   *
   * @param originalBytes Original PDF bytes.
   * @returns Reparied and reprocessed bytes.
   */
  reprocessPdf(
    originalBytes: ArrayBuffer
  ): Observable<Uint8Array<ArrayBufferLike>> {
    // Just open and rewrite the pdf to attempt to correct encoding
    // abnormalities. This is done by just calling without extra operation arguments.
    return this.callArgumentsOnFile(originalBytes);
  }

  /**
   * Attempts to call qpdf with the provdied arguments.
   *
   * @param bytes Input PDF bytes.
   * @param args An array of arguments to provide to qpdf. Optional, if none are
   * provided the file will just rewrite.
   * @returns Result bytes.
   */
  private callArgumentsOnFile(
    originalBytes: ArrayBuffer,
    args: string[] = []
  ): Observable<Uint8Array<ArrayBufferLike>> {
    // Just open and rewrite the pdf to attempt to correct encoding abnormalities.
    this.init();
    assertExists(
      this.instance$,
      'qpdf instance observable does not exist and is required.'
    );
    // Unique ID for the file operation.
    const operationId = uuid();
    const inputFilename = `${operationId}_input.pdf`;
    const outputFilename = `${operationId}_output.pdf`;
    // Build the full arguments for qpdf.
    const executionArguments = [...args];
    executionArguments.unshift(inputFilename); // Input as first arg.
    executionArguments.push(outputFilename); // Output as last arg.
    // Call pdf with a basic in and out flow.
    const outputFile$ = this.instance$.pipe(
      // Load the file bytes into EmscriptenFS
      tap((qpdf) => {
        // The document needs to be put into the virtualized filesystem first.
        // Type EmpscriptenFS does not seem to include the writeFile method that exists:
        // https://emscripten.org/docs/api_reference/Filesystem-API.html#FS.writeFile
        const fs = qpdf.FS as unknown as {
          writeFile(path: string, data: string | Uint8Array): void;
        };
        assertExists(fs.writeFile, 'EmscriptenFS must provide writeFile().');
        fs.writeFile(inputFilename, new Uint8Array(originalBytes));
      }),
      // Apply the provided arguments.
      tap((qpdf) => {
        qpdf.callMain(executionArguments);
      }),
      // Collect the new file.
      map((qpdf) => {
        return qpdf.FS.readFile(outputFilename);
      })
    );
    // Return the output result.
    return outputFile$;
  }
}
